import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from "recharts";
import {
  Server,
  Radio,
  Building2,
  TrendingUp,
  Activity,
  DollarSign,
  Eye,
  ArrowUpRight,
  ArrowDownRight,
  Calendar,
  Zap,
  Globe,
  Gauge,
} from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../contexts/I18nContext";

export default function DashboardPage() {
  const { api } = useAuth();
  const { t } = useI18n();
  const [overview, setOverview] = useState(null);
  const [timeseries, setTimeseries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState("7");
  const [customRange, setCustomRange] = useState({ start: "", end: "" });
  const [showCustom, setShowCustom] = useState(false);
  const [liveQPS, setLiveQPS] = useState(null);
  const [regionalQPS, setRegionalQPS] = useState(null);
  const [widgetConfig, setWidgetConfig] = useState(null);

  const fetchLiveQPS = useCallback(async () => {
    try {
      const [qpsRes, regionalRes] = await Promise.all([
        api.get("/qps/live"),
        api.get("/qps/regional-dashboard"),
      ]);
      setLiveQPS(qpsRes.data);
      setRegionalQPS(regionalRes.data);
    } catch (e) {
      // Silent fail
    }
  }, [api]);

  useEffect(() => {
    fetchData();
    fetchLiveQPS();
    
    // Auto-refresh QPS every 5 seconds
    const interval = setInterval(fetchLiveQPS, 5000);
    return () => clearInterval(interval);
  }, [timeRange, fetchLiveQPS]);

  const fetchData = async () => {
    try {
      let timeseriesUrl = `/analytics/timeseries?days=${timeRange}`;
      
      // If custom range is set and timeRange is "custom"
      if (timeRange === "custom" && customRange.start && customRange.end) {
        timeseriesUrl = `/analytics/timeseries?start_date=${customRange.start}&end_date=${customRange.end}`;
      }
      
      const [overviewRes, timeseriesRes, widgetRes] = await Promise.all([
        api.get("/analytics/overview"),
        api.get(timeseriesUrl),
        api.get("/settings/dashboard/layout").catch(() => ({ data: { widgets: [] } })),
      ]);
      setOverview(overviewRes.data);
      setTimeseries(timeseriesRes.data);
      // Build widget visibility map: { widget_id: boolean }
      const wc = {};
      (widgetRes.data.widgets || []).forEach(w => { wc[w.id] = w.visible !== false; });
      setWidgetConfig(wc);
    } catch (error) {
      toast.error("Failed to fetch analytics data");
    } finally {
      setLoading(false);
    }
  };

  // Helper: check if a widget is visible (default to true if not configured)
  const isVisible = (widgetId) => {
    if (!widgetConfig || Object.keys(widgetConfig).length === 0) return true;
    return widgetConfig[widgetId] !== false;
  };

  const handleCustomRangeApply = () => {
    if (!customRange.start || !customRange.end) {
      toast.error("Please select both start and end dates");
      return;
    }
    if (new Date(customRange.start) > new Date(customRange.end)) {
      toast.error("Start date must be before end date");
      return;
    }
    setTimeRange("custom");
    setShowCustom(false);
    fetchData();
  };

  const handleTimeRangeChange = (value) => {
    if (value === "custom") {
      setShowCustom(true);
    } else {
      setTimeRange(value);
      setShowCustom(false);
    }
  };

  const statCards = overview
    ? [
        {
          widgetId: "total_ssps",
          title: "Total SSPs",
          value: overview.total_ssps,
          subValue: `${overview.active_ssps} active`,
          icon: Server,
        },
        {
          widgetId: "total_dsps",
          title: "Total DSPs",
          value: overview.total_dsps,
          subValue: `${overview.active_dsps} active`,
          icon: Radio,
        },
        {
          widgetId: "companies",
          title: "Companies",
          value: overview.total_companies,
          subValue: "Partners",
          icon: Building2,
        },
        {
          widgetId: "total_requests",
          title: "Total Requests",
          value: formatNumber(overview.total_requests),
          subValue: "All time",
          icon: Activity,
        },
        {
          widgetId: "impressions",
          title: "Impressions",
          value: formatNumber(overview.total_impressions),
          subValue: overview.total_requests > 0
            ? `${(overview.total_impressions / overview.total_requests * 100).toFixed(1)}% fill rate`
            : "Delivered",
          icon: Eye,
        },
        {
          widgetId: "revenue",
          title: "Revenue",
          value: `$${formatNumber(overview.total_revenue)}`,
          subValue: "Total earnings",
          icon: DollarSign,
        },
      ]
    : [];

  function formatNumber(num) {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + "M";
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + "K";
    }
    return num.toString();
  }

  function formatQPS(val) {
    if (val === undefined || val === null) return "0";
    if (val >= 1000) return `${(val / 1000).toFixed(1)}K`;
    if (val >= 1) return val.toFixed(0);
    if (val > 0) return val.toFixed(2);
    return "0";
  }

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-popover text-popover-foreground border rounded-lg p-3 shadow-lg">
          <p className="text-sm font-medium mb-2">{label}</p>
          {payload.map((entry, index) => (
            <div key={index} className="flex items-center gap-2 text-sm">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: entry.color }}
              />
              <span className="text-muted-foreground">{entry.name}:</span>
              <span className="font-mono font-medium">
                {entry.name === "revenue"
                  ? `$${entry.value.toFixed(2)}`
                  : formatNumber(entry.value)}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="dashboard-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t("dashboard.title", "Dashboard")}</h1>
          <p className="text-muted-foreground">
            {t("dashboard.overview", "Overview of your oRTB exchange performance")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={timeRange === "custom" ? "custom" : timeRange} onValueChange={handleTimeRangeChange}>
            <SelectTrigger className="w-[180px]" data-testid="time-range-select">
              <SelectValue placeholder="Select time range" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="yesterday">Yesterday</SelectItem>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="14">Last 14 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="custom">Custom Range</SelectItem>
            </SelectContent>
          </Select>
          
          {/* Custom Date Range Popover */}
          <Popover open={showCustom} onOpenChange={setShowCustom}>
            <PopoverTrigger asChild>
              <Button 
                variant="outline" 
                size="icon"
                className={timeRange === "custom" ? "border-accent" : ""}
              >
                <Calendar className="h-4 w-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80" align="end">
              <div className="space-y-4">
                <h4 className="font-medium">Custom Date Range</h4>
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label>Start Date</Label>
                    <Input
                      type="date"
                      value={customRange.start}
                      onChange={(e) => setCustomRange({ ...customRange, start: e.target.value, end: customRange.end && customRange.end < e.target.value ? "" : customRange.end })}
                      data-testid="custom-start-date"
                      className="w-full"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>End Date</Label>
                    <Input
                      type="date"
                      value={customRange.end}
                      min={customRange.start || undefined}
                      onChange={(e) => setCustomRange({ ...customRange, end: e.target.value })}
                      data-testid="custom-end-date"
                      className="w-full"
                    />
                  </div>
                </div>
                <Button onClick={handleCustomRangeApply} className="w-full">
                  Apply Range
                </Button>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* Stats Grid */}
      {statCards.filter(s => isVisible(s.widgetId)).length > 0 && (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {statCards.filter(s => isVisible(s.widgetId)).map((stat, index) => (
          <Card
            key={index}
            className="stat-card"
            data-testid={`stat-card-${stat.title.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <div className="flex items-start justify-between">
              <div className="h-10 w-10 rounded-lg bg-accent/10 flex items-center justify-center">
                <stat.icon className="h-5 w-5 text-accent" />
              </div>
            </div>
            <div className="mt-3">
              <p className="stat-card-value">{stat.value}</p>
              <p className="stat-card-label">{stat.title}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{stat.subValue}</p>
            </div>
          </Card>
        ))}
      </div>
      )}

      {/* Regional QPS Summary */}
      {regionalQPS && (
        <div className="space-y-4" data-testid="regional-qps-section">
          {/* Total QPS Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="border-l-4 border-l-slate-500" data-testid="total-qps-limit">
              <CardContent className="pt-4 pb-3 px-4">
                <p className="text-xs text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                  <Gauge className="h-3.5 w-3.5" /> Software QPS Limit
                </p>
                <p className="text-2xl font-bold mt-1">
                  {regionalQPS.total.qps_limit > 0 ? formatQPS(regionalQPS.total.qps_limit) : "—"}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {regionalQPS.total.utilization > 0 ? `${regionalQPS.total.utilization}% utilized` : "total capacity"}
                </p>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-blue-500" data-testid="total-incoming-qps">
              <CardContent className="pt-4 pb-3 px-4">
                <p className="text-xs text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
                  Incoming QPS
                </p>
                <p className="text-2xl font-bold text-blue-600 mt-1">
                  {formatQPS(regionalQPS.total.incoming_qps)}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">total queries/sec</p>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-emerald-500" data-testid="total-valid-qps">
              <CardContent className="pt-4 pb-3 px-4">
                <p className="text-xs text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                  <Zap className="h-3.5 w-3.5" /> Valid Incoming QPS
                </p>
                <p className="text-2xl font-bold text-emerald-600 mt-1">
                  {formatQPS(regionalQPS.total.valid_incoming_qps)}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {liveQPS ? `${liveQPS.fill_rate}% fill rate` : "with bids"}
                </p>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-violet-500" data-testid="total-outgoing-qps">
              <CardContent className="pt-4 pb-3 px-4">
                <p className="text-xs text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                  <ArrowUpRight className="h-3.5 w-3.5" /> Outgoing QPS
                </p>
                <p className="text-2xl font-bold text-violet-600 mt-1">
                  {formatQPS(regionalQPS.total.outgoing_qps)}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">to DSP endpoints</p>
              </CardContent>
            </Card>
          </div>

          {/* Region-wise Breakdown Table */}
          {regionalQPS.regions && regionalQPS.regions.length > 0 && (
            <Card data-testid="region-breakdown-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Globe className="h-4 w-4" />
                  Region-wise QPS
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="pb-2 font-medium text-muted-foreground">Region</th>
                        <th className="pb-2 font-medium text-muted-foreground text-right">QPS Limit</th>
                        <th className="pb-2 font-medium text-muted-foreground text-right">Incoming QPS</th>
                        <th className="pb-2 font-medium text-muted-foreground text-right">Valid QPS</th>
                        <th className="pb-2 font-medium text-muted-foreground text-right">Outgoing QPS</th>
                        <th className="pb-2 font-medium text-muted-foreground text-right">Utilization</th>
                        <th className="pb-2 font-medium text-muted-foreground text-right">SSPs</th>
                      </tr>
                    </thead>
                    <tbody>
                      {regionalQPS.regions.map((r) => (
                        <tr key={r.region} className="border-b last:border-0 hover:bg-muted/30" data-testid={`region-row-${r.region}`}>
                          <td className="py-2.5 font-medium">{r.region_name}</td>
                          <td className="py-2.5 text-right font-mono">{r.qps_limit > 0 ? formatQPS(r.qps_limit) : "—"}</td>
                          <td className="py-2.5 text-right font-mono text-blue-600">{formatQPS(r.incoming_qps)}</td>
                          <td className="py-2.5 text-right font-mono text-emerald-600">{formatQPS(r.valid_incoming_qps)}</td>
                          <td className="py-2.5 text-right font-mono text-violet-600">{formatQPS(r.outgoing_qps)}</td>
                          <td className="py-2.5 text-right">
                            {r.qps_limit > 0 ? (
                              <span className={`font-mono text-xs px-1.5 py-0.5 rounded ${
                                r.utilization > 80 ? "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400" :
                                r.utilization > 50 ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400" :
                                "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
                              }`}>
                                {r.utilization}%
                              </span>
                            ) : "—"}
                          </td>
                          <td className="py-2.5 text-right text-muted-foreground">{r.active_ssp_count}/{r.ssp_count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Requests Chart */}
        {isVisible("requests_chart") && (
        <Card data-testid="requests-chart">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5 text-accent" />
              {t("dashboard.requests_impressions", "Requests & Impressions")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={timeseries}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                  <XAxis
                    dataKey="timestamp"
                    className="text-xs"
                    tick={{ fill: "hsl(var(--muted-foreground))" }}
                  />
                  <YAxis
                    className="text-xs"
                    tick={{ fill: "hsl(var(--muted-foreground))" }}
                    tickFormatter={formatNumber}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Line
                    type="monotone"
                    dataKey="requests"
                    name="Requests"
                    stroke="hsl(var(--chart-1))"
                    strokeWidth={2}
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="impressions"
                    name="Impressions"
                    stroke="hsl(var(--chart-2))"
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
        )}

        {/* Revenue Chart */}
        {isVisible("revenue_chart") && (
        <Card data-testid="revenue-chart">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-accent" />
              {t("dashboard.revenue", "Revenue")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={timeseries}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                  <XAxis
                    dataKey="timestamp"
                    className="text-xs"
                    tick={{ fill: "hsl(var(--muted-foreground))" }}
                  />
                  <YAxis
                    className="text-xs"
                    tick={{ fill: "hsl(var(--muted-foreground))" }}
                    tickFormatter={(v) => `$${v}`}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar
                    dataKey="revenue"
                    name="revenue"
                    fill="hsl(var(--chart-1))"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
        )}
      </div>
    </div>
  );
}
